/**
 * Authentication Context & Hooks
 *
 * Provides authentication state throughout the app.
 * Uses server-side session authentication via Okta OAuth.
 */

import { createContext, useContext, useState, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

interface User {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  role: string;
  developerId: string | null;
  developer?: {
    id: string;
    displayName: string;
    githubUsername: string | null;
  } | null;
}

interface AuthContextValue {
  user: User | null;
  /** True once roles come from Okta groups; until then everyone may edit. */
  rolesManaged: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (redirectTo?: string) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Fetch current user
  const { data, isLoading } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: () =>
      api.get<{ authenticated: boolean; user: User | null; rolesManaged?: boolean }>(
        "/auth/okta/me",
      ),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  const user = data?.user ?? null;
  const isAuthenticated = data?.authenticated ?? false;
  const rolesManaged = data?.rolesManaged ?? false;

  // Login - redirect to Okta
  const login = (redirectTo?: string) => {
    const params = redirectTo
      ? `?redirectTo=${encodeURIComponent(redirectTo)}`
      : "";
    window.location.href = `/api/auth/okta/login${params}`;
  };

  // Logout - destroy session
  const logout = async () => {
    setIsLoggingOut(true);
    try {
      const response = await api.post<{ success: boolean; logoutUrl?: string }>(
        "/auth/okta/logout",
      );

      // Clear auth cache
      queryClient.setQueryData(["auth", "me"], {
        authenticated: false,
        user: null,
      });
      queryClient.invalidateQueries();

      // Redirect to Okta logout if URL provided
      if (response.logoutUrl) {
        window.location.href = response.logoutUrl;
      } else {
        window.location.href = "/login";
      }
    } catch (error) {
      console.error("Logout error:", error);
      // Still redirect on error
      window.location.href = "/login";
    } finally {
      setIsLoggingOut(false);
    }
  };

  const value: AuthContextValue = {
    user,
    rolesManaged,
    isLoading: isLoading || isLoggingOut,
    isAuthenticated,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/**
 * Hook that returns true if user is authenticated
 */
export function useIsAuthenticated(): boolean {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
}

/** `User.role` is "viewer", "manager" or "admin"; only admins change data. */
export function isAdminRole(role: string | null | undefined): boolean {
  return role === "admin";
}

/**
 * True when the signed-in user may use admin controls: an admin, or anyone
 * while roles aren't managed (OKTA_ADMIN_GROUP unset on the server).
 */
export function useIsAdmin(): boolean {
  const { user, rolesManaged } = useAuth();
  return !rolesManaged || isAdminRole(user?.role);
}

/**
 * Hook that returns current user or null
 */
export function useUser(): User | null {
  const { user } = useAuth();
  return user;
}
